import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Interceptor global de 401: tenta renovar via /auth/refresh. Se falhar, redireciona
// para /login?expired=1 com mensagem clara, em vez de mostrar "Não autenticado" num formulário.
let refreshInflight = null;
const AUTH_SKIP = /\/auth\/(login|signup|logout|refresh|forgot-password|reset-password|request-new-link|link-status)/;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const url = original?.url || "";
    if (status !== 401 || original._retry || AUTH_SKIP.test(url)) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      refreshInflight = refreshInflight || api.post("/auth/refresh").finally(() => { refreshInflight = null; });
      await refreshInflight;
      return api(original);
    } catch {
      if (typeof window !== "undefined") {
        const path = window.location.pathname + window.location.search;
        const onLogin = window.location.pathname.startsWith("/login") || window.location.pathname.startsWith("/o/") || window.location.pathname === "/cadastro";
        if (!onLogin && !/\/auth\/me/.test(url)) {
          const next = encodeURIComponent(path);
          window.location.replace(`/login?expired=1&next=${next}`);
        }
      }
      return Promise.reject(error);
    }
  },
);

export function formatApiError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (!err?.response && (err?.message === "Network Error" || err?.code === "ERR_NETWORK")) return "Sem conexão com o servidor. Verifique a internet e tente de novo.";
  if ([502, 503, 504, 520, 521, 522, 523, 524].includes(status) || (typeof data === "string" && /<html|cloudflare/i.test(data)))
    return "O servidor está reiniciando ou demorou para responder. Aguarde alguns segundos e tente salvar de novo.";
  const detail = data?.detail;
  if (detail == null) return status >= 500 ? "Erro no servidor. Tente novamente em instantes." : (err?.message || "Erro inesperado. Tente novamente.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const STATUS_META = {
  parado: {
    label: "Cadastro",
    short: "Cadastro",
    classes: "bg-zinc-900 text-zinc-300 border-zinc-700",
    dot: "bg-zinc-400",
    tint: "bg-zinc-800/70 text-zinc-300 border-zinc-700",
    subtitle: "Aguardando início",
    cardTitle: "Em cadastro",
    step: 1,
  },
  nao_realizado: {
    label: "Não realizado",
    short: "Não realizado",
    classes: "bg-red-950/80 text-red-300 border-red-900",
    dot: "bg-red-400",
    tint: "bg-red-950/60 text-red-300 border-red-900",
    subtitle: "Encerrada sem serviço",
    cardTitle: "Não realizados",
    step: 5,
  },
  aguardando_aprovacao: {
    label: "Aguardando aprovação",
    short: "Aprovação",
    classes: "bg-amber-950/80 text-amber-300 border-amber-700",
    dot: "bg-amber-400",
    tint: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    subtitle: "Orçamentos",
    cardTitle: "Aguardando aprovação",
    step: 2,
  },
  em_andamento: {
    label: "Em andamento",
    short: "Em andamento",
    classes: "bg-sky-950/70 text-sky-300 border-sky-700",
    dot: "bg-sky-400",
    tint: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    subtitle: "Dentro do prazo",
    cardTitle: "Em andamento",
    step: 3,
  },
  pronto: {
    label: "Pronto / Concluído",
    short: "Pronto",
    classes: "bg-green-900/60 text-green-300 border-green-500",
    dot: "bg-green-400",
    tint: "bg-green-500/15 text-green-300 border-green-500/40",
    subtitle: "Cliente já avisado",
    cardTitle: "Prontos para retirada",
    step: 4,
  },
  recusado: {
    label: "Recusado",
    short: "Recusado",
    classes: "bg-red-950/80 text-red-300 border-red-700",
    dot: "bg-red-500",
    tint: "bg-red-500/15 text-red-300 border-red-500/40",
    subtitle: "Orçamento recusado",
    cardTitle: "Recusados",
    step: 2,
  },
  entregue: {
    label: "Entregue",
    short: "Entregue",
    classes: "bg-teal-950/70 text-teal-300 border-teal-600",
    dot: "bg-teal-400",
    tint: "bg-teal-500/15 text-teal-300 border-teal-500/40",
    subtitle: "Veículo retirado",
    cardTitle: "Entregues",
    step: 5,
  },
};

export const STATUS_ORDER = ["parado", "aguardando_aprovacao", "em_andamento", "pronto", "entregue", "recusado", "nao_realizado"];

export const CHECKLIST_META = {
  ok: { label: "OK", classes: "bg-emerald-950/80 text-emerald-300 border-emerald-700" },
  trocar: { label: "Trocar", classes: "bg-red-950/80 text-red-300 border-red-800" },
  verificar: { label: "Verificar", classes: "bg-amber-950/80 text-amber-300 border-amber-700" },
  na: { label: "N/A", classes: "bg-zinc-900 text-zinc-500 border-zinc-800" },
};

export const CHECKLIST_VALUES = ["ok", "trocar", "verificar", "na"];

export function brl(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function shopLogoUrl(shopId) {
  if (!shopId) return null;
  return `${API}/shops/${shopId}/logo`;
}


const _p2 = (n) => String(n).padStart(2, "0");

export function fmtExpected(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const wd = d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
  const h = d.getHours();
  const m = d.getMinutes();
  return `${wd}, ${_p2(d.getDate())}/${_p2(d.getMonth() + 1)} às ${h}h${m ? _p2(m) : ""}`;
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${_p2(d.getDate())}/${_p2(d.getMonth() + 1)}/${d.getFullYear()} às ${_p2(d.getHours())}:${_p2(d.getMinutes())}`;
}

export function isExpectedOverdue(order) {
  return !!order?.expected_at && order.status !== "pronto" && order.status !== "entregue" && new Date(order.expected_at) < new Date();
}

export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${_p2(d.getMonth() + 1)}-${_p2(d.getDate())}T${_p2(d.getHours())}:${_p2(d.getMinutes())}`;
}

export function fromLocalInput(v) {
  return v ? new Date(v).toISOString() : null;
}
